/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointSecurityComponent" />
import * as React from "react";

import { SecurityNamespaceConstants } from "DistributedTaskUI/SecurityComponent/Constants";
import { ServiceEndpointActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsCreator";
import { IServiceEndpointComponentState, ServiceEndpointStore } from "DistributedTaskUI/ServiceEndpoints/Details/Stores/ServiceEndpointStore";
import * as RoleAssignmentComponentSecurityControl from "DistributedTaskUI/SecurityComponent/UserPermissionsAuthorization/Components/RoleAssignmentComponentSecurityControl";
import * as ProjectAuthorizationComponent from "DistributedTaskUI/SecurityComponent/ProjectPermissionsAuthorization/Components/ProjectAuthorizationComponent";
import { PipelinesAuthorizationComponent } from "DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Components/PipelinesAuthorizationComponent";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageService, IPageData } from "VSS/Platform/Page";
import { getTFSPageData } from "Tfs/Platform/Page";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Details/Resources";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

export interface IServiceEndpointSecurityComponentProps {
}

export class ServiceEndpointSecurityComponent extends VssComponent<IServiceEndpointSecurityComponentProps, IServiceEndpointComponentState>{
    constructor(props: IServiceEndpointSecurityComponentProps, context: IVssComponentContext) {
        super(props, context);
        document.title = Resources.ServiceConnectionSecurityPageTitle;
        this._actionCreator = ActionCreatorManager.GetActionCreator<ServiceEndpointActionsCreator>(ServiceEndpointActionsCreator, "service-endpoints-section-actionscreator");
        this._store = StoreManager.GetStore<ServiceEndpointStore>(ServiceEndpointStore, ServiceEndpointStore.getKey());
        this.state = this._store.getState();
        this.context = context;
        this._canEditProjectView = this._actionCreator.checkManageEndpointPermission(this.state.serviceEndpoint.id, SecurityNamespaceConstants.AdminPermission);
        this._canOpenAccess = this._actionCreator.checkProjectLevelManagePermission(SecurityNamespaceConstants.AdminPermission);
        this._canEditOrganizationView = this._actionCreator.checkManageProjectSharingPermission(this.state.serviceEndpoint.id, SecurityNamespaceConstants.AdminPermission);
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._onStoreChanged);
        let endpointSecurityPageBackButton: HTMLElement = document.getElementById("__bolt-endpoint-level-security-page-id");
        if (endpointSecurityPageBackButton) {
            endpointSecurityPageBackButton.focus();
        }  
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
    }

    private _onStoreChanged = (): void => {
        this.setState(this._store.getState());
    }

    public render(): React.ReactNode {
        return (
            <div className="service-connection-security-tab page-content page-content-top">
                {this._getUserPermissionsView()}
                {this._getPipelinePermissionsView()}
                {this._getProjectReferencesView()}
            </div>
        );
    }

    private _getUserPermissionsView(): JSX.Element {
        let isShareServiceEndpointsAcrossProjectsNewUxFFEnabled = isFeatureFlagEnabled(this.context.pageContext, "ServiceEndpoints.ShareAcrossProjectsNewUx", false);
        const serviceEndpointsComponentProps: RoleAssignmentComponentSecurityControl.IRoleAssignmentSecurityProps = {
            serviceInstanceId: "00025394-6065-48ca-87d9-7f5672854ef7",
            scopeId: SecurityNamespaceConstants.ProjectScopeId.valueOf(),
            userId: this._getUserId(),
            resourceId: this._getResourceId(),
            canEditProjectView: this._canEditProjectView,
            canEditOrganizationView: this._canEditOrganizationView,
            manageRolesPermission: SecurityNamespaceConstants.AdminPermission.valueOf(),
            noPermissionsMessage: Resources.noPermissionsMessage,
            title: Resources.title,
            showAvatars: true,
            enableToggleInheritence: true,
            endpointId: this.state.serviceEndpoint.id,
            className: "security-user-permissions-dialog",
            identityTypes: ["user", "group", "servicePrincipal"],
            operationScopes: ["ims", "source"],
            roleScope: "Project",
            enableTogglingOrganizationButton: isShareServiceEndpointsAcrossProjectsNewUxFFEnabled
        };
        return (
            <RoleAssignmentComponentSecurityControl.RoleAssignmentComponentSecurityControl {...serviceEndpointsComponentProps} />
        );
    }

    private _getPipelinePermissionsView(): JSX.Element {
        return (
            <PipelinesAuthorizationComponent
                resourceId={this.state.serviceEndpoint.id}
                resourceType="endpoint"
                noAuthorizedPipelinesViewLearnMoreLink={this._learnMoreUrl}
                noRestrictionsViewLearnMoreLink={this._learnMoreUrl}
                canRestrictAccess={this._canEditProjectView || this._canEditOrganizationView}
                canOpenAccess={this._canOpenAccess}
            />
        );
    }

    private _getProjectReferencesView(): JSX.Element {
        let isShareServiceEndpointsAcrossProjectsFFEnabled = isFeatureFlagEnabled(this.context.pageContext, "WebAccess.ServiceEndpoints.ShareAcrossProjects", false);
        if (isShareServiceEndpointsAcrossProjectsFFEnabled) {
            const projectAuthorizationSecurityProps: ProjectAuthorizationComponent.IProjectAuthorizationSecurityProps = {
            endpoint: this.state.serviceEndpoint,
            canShareProjects: this._canEditOrganizationView,
            updateServiceEndpointSharedFlag: this._updateServiceEndpointSharedFlag,
            noProjectPermissionsViewLearnMoreLink: this._learnMoreUrl
            };
            return (
                <ProjectAuthorizationComponent.ProjectAuthorizationComponent {...projectAuthorizationSecurityProps} />
            );
        } else {
            return <div />;
        }
    }

    private _getSpinner = (): JSX.Element => {
        return <Spinner className="scroll-hidden" size={SpinnerSize.large} />;
    }

    private _getUserId(): string | null {
        const pageService: IVssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData: IPageData | undefined = pageService.getData();

        if (pageData) {
            return pageData.user.id;
        }
        else {
            // something is broken if we don't have a user id.
            return null;
        }
    }

    private _getResourceId(): string {
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (tfsPageData && tfsPageData.project) {
            let projectId = tfsPageData.project.id;
            return projectId + "_" + this.state.serviceEndpoint.id;
        }

        return "";
    }

    private _updateServiceEndpointSharedFlag = (serviceEndpoint: ServiceEndpoint) => {
        this._actionCreator.updateServiceEndpointSharedFlag(serviceEndpoint);
    }

    public context: IVssComponentContext;
    private _store: ServiceEndpointStore;
    private _actionCreator: ServiceEndpointActionsCreator;
    private _learnMoreUrl = "https://aka.ms/SCAuthorization";
    private _canEditProjectView: boolean;
    private _canEditOrganizationView: boolean;
    private _canOpenAccess: boolean;
}
